import ContextSelector from "@components/analytics/context-selector/ContextSelector";
import DownloadCsvButton from "@components/analytics/download-csv-button";
import MultipleSelectorWithCount from "@components/analytics/multiple-selector-with-count/MultipleSelectorWithCount";
import PeriodSelector, { PERIODS } from "@components/analytics/period-selector/PeriodSelector";
import ProspectSelector from "@components/analytics/prospect-selector/ProspectSelector";
import { AppHeader } from "@components/app-header";
import MultipleCommunitySelector from "@components/common/multiple-community-selector";
import MultipleOrganizationSelector from "@components/common/multiple-organization-selector";
import { ProspectStatuses } from "@constants/ProspectStatuses";
import { USER_ROLES } from "@constants/Roles";
import { PeekDivider, PeekGrid, PeekStack, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { useAuthContext } from "@stores/AuthContext";
import { format, sub } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Outlet, useNavigate } from "react-router-dom";
import { messages } from "./messages";
const DATE_FORMAT = "yyyy-MM-dd";
export var ConversionContext;
(function (ConversionContext) {
    ConversionContext["CONVERSION"] = "conversion";
})(ConversionContext || (ConversionContext = {}));
export var AnalyticsContext;
(function (AnalyticsContext) {
    AnalyticsContext["ENGAGEMENT"] = "engagement";
    AnalyticsContext["CONTENT_CREATION"] = "content-creation";
})(AnalyticsContext || (AnalyticsContext = {}));
export const AnalyticsLayout = () => {
    var _a, _b;
    const intl = useIntl();
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const initialPeriod = useMemo(() => {
        const endDate = format(sub(new Date(), { days: 1 }), DATE_FORMAT);
        const startDate = format(sub(new Date(), { days: PERIODS[0] }), DATE_FORMAT);
        return { startDate, endDate };
    }, []);
    const [period, setPeriod] = useState(initialPeriod);
    const [analyticsContext, setAnalyticsContext] = useState(null);
    const [selectedCommunities, setSelectedCommunities] = useState([]);
    const [selectedOrganizations, setSelectedOrganizations] = useState([]);
    const [selectedProspectStatuses, setSelectedProspectStatuses] = useState(Object.values(ProspectStatuses));
    const plans = useMemo(() => {
        var _a;
        const plans = (_a = user === null || user === void 0 ? void 0 : user.communities) === null || _a === void 0 ? void 0 : _a.map((community) => community.plan);
        return Array.from(new Set(plans));
    }, [user]);
    const [selectedPlans, setSelectedPlans] = useState(plans);
    const [layoutConfig, setLayoutConfig] = useState({
        pageTitle: intl.formatMessage(messages.analyticsTitle),
    });
    const filterCommunitiesByOrganization = (organizationIds, communities) => {
        return communities.filter((community) => organizationIds.includes(community.organization._id));
    };
    const handleContextChange = (option) => {
        setAnalyticsContext(option);
        navigate(option);
    };
    const handleChangeSelectedPlans = (newPlans) => {
        if (newPlans.length === 0)
            return;
        setSelectedPlans(newPlans);
    };
    const handleSelectProspectStatuses = (newProspectStatuses) => {
        if (newProspectStatuses.length === 0)
            return;
        if (newProspectStatuses.includes(ProspectStatuses.ALL)) {
            setSelectedProspectStatuses(Object.values(ProspectStatuses));
            return;
        }
        else if (newProspectStatuses.includes(ProspectStatuses.IDENTIFIED)) {
            setSelectedProspectStatuses([ProspectStatuses.IDENTIFIED, ProspectStatuses.APPLIED]);
            return;
        }
        else {
            setSelectedProspectStatuses([ProspectStatuses.APPLIED]);
        }
    };
    const communitiesAvailableForUser = useMemo(() => {
        const auxCommunities = (user === null || user === void 0 ? void 0 : user.roleId) === USER_ROLES.Owner
            ? filterCommunitiesByOrganization(selectedOrganizations, user.communities).filter((community) => selectedPlans.includes(community.plan))
            : user.communities.filter((community) => selectedPlans.includes(community.plan));
        return auxCommunities;
    }, [selectedOrganizations, selectedPlans]);
    useEffect(() => {
        setSelectedCommunities(selectedCommunities.filter((communityId) => communitiesAvailableForUser.map((community) => community._id).includes(communityId)));
    }, [communitiesAvailableForUser]);
    useEffect(() => {
        const url = window.location.href;
        const urlParts = url.split("/");
        const option = urlParts[urlParts.length - 1];
        switch (option) {
            case ConversionContext.CONVERSION:
                if ((user === null || user === void 0 ? void 0 : user.hasSyncPms) || (user === null || user === void 0 ? void 0 : user.hasSyncCrm)) {
                    setAnalyticsContext(ConversionContext.CONVERSION);
                }
                else {
                    setAnalyticsContext(AnalyticsContext.ENGAGEMENT);
                }
                break;
            case AnalyticsContext.ENGAGEMENT:
                setAnalyticsContext(AnalyticsContext.ENGAGEMENT);
                break;
            case AnalyticsContext.CONTENT_CREATION:
                setAnalyticsContext(AnalyticsContext.CONTENT_CREATION);
                break;
            default:
                setAnalyticsContext(AnalyticsContext.ENGAGEMENT);
                break;
        }
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekGrid, { item: true, xs: 12, sx: { userSelect: "none" } },
            React.createElement(PeekGrid, { container: true, direction: "row", spacing: 2, justifyContent: "space-between" },
                React.createElement(PeekGrid, { item: true, xs: 7 },
                    React.createElement(PeekStack, { direction: "row", spacing: 4, alignItems: "center" },
                        (layoutConfig === null || layoutConfig === void 0 ? void 0 : layoutConfig.pageTitle) && React.createElement(AppHeader, { headerLabel: layoutConfig === null || layoutConfig === void 0 ? void 0 : layoutConfig.pageTitle }),
                        React.createElement(PeekStack, { direction: "row", spacing: 2 },
                            React.createElement(ContextSelector, { context: analyticsContext, setContext: handleContextChange })))),
                React.createElement(PeekGrid, { item: true, xs: 5 },
                    React.createElement(PeekStack, { spacing: 2, height: "100%", direction: "row", justifyContent: "flex-end", alignItems: "center" },
                        React.createElement(PeriodSelector, { onChange: (newPeriod) => setPeriod({
                                startDate: format(newPeriod.startDate, DATE_FORMAT),
                                endDate: format(newPeriod.endDate, DATE_FORMAT),
                            }), selectedPeriod: {
                                startDate: new Date(new Date(period.startDate).getUTCFullYear(), new Date(period.startDate).getUTCMonth(), new Date(period.startDate).getUTCDate()),
                                endDate: new Date(new Date(period.endDate).getUTCFullYear(), new Date(period.endDate).getUTCMonth(), new Date(period.endDate).getUTCDate()),
                            } }),
                        React.createElement(DownloadCsvButton, { period: {
                                startDate: new Date(new Date(period.startDate).getUTCFullYear(), new Date(period.startDate).getUTCMonth(), new Date(period.startDate).getUTCDate()),
                                endDate: new Date(new Date(period.endDate).getUTCFullYear(), new Date(period.endDate).getUTCMonth(), new Date(period.endDate).getUTCDate()),
                            }, communityIds: selectedCommunities.filter(Boolean), analyticsContext: analyticsContext, height: theme.spacing(4), width: theme.spacing(16) }))),
                React.createElement(PeekGrid, { item: true, xs: 5 },
                    React.createElement(PeekStack, { direction: "row", spacing: theme.spacing(2), flexGrow: 1, maxWidth: "fit-content", minHeight: theme.spacing(4) },
                        !!((_a = user === null || user === void 0 ? void 0 : user.organizations) === null || _a === void 0 ? void 0 : _a.length) && (user === null || user === void 0 ? void 0 : user.roleId) === USER_ROLES.Owner && (React.createElement(MultipleOrganizationSelector, { placeholder: !(selectedOrganizations === null || selectedOrganizations === void 0 ? void 0 : selectedOrganizations.length)
                                ? intl.formatMessage(messages.organizationSelectorPlaceholder)
                                : intl.formatMessage(messages.organizationSelectorPlaceholderAddMore), organizations: user.organizations, selectedOrganizations: selectedOrganizations, onSelectionChange: setSelectedOrganizations })),
                        !!((_b = user === null || user === void 0 ? void 0 : user.communities) === null || _b === void 0 ? void 0 : _b.length) && (React.createElement(PeekStack, { direction: "row" },
                            React.createElement(MultipleSelectorWithCount, { isCombined: "left", selectedItems: selectedPlans, items: plans, setItems: handleChangeSelectedPlans, label: intl.formatMessage(messages.plansSelectorLabel), renderValue: (_value) => (React.createElement(PeekTypography, null, intl.formatMessage(messages.plansSelectorLabel))) }),
                            React.createElement(PeekDivider, { orientation: "vertical", light: true, sx: { height: "calc(100% - 8px)", my: "auto" } }),
                            React.createElement(MultipleCommunitySelector, { isCombined: "right", feature: "Analytics" /* PolicyActions.ANALYTICS */, placeholder: !(selectedCommunities === null || selectedCommunities === void 0 ? void 0 : selectedCommunities.length)
                                    ? intl.formatMessage(messages.communitySelectorPlaceholder)
                                    : intl.formatMessage(messages.communitySelectorPlaceholderAddMore), communities: communitiesAvailableForUser, selectedCommunities: selectedCommunities, onSelectionChange: setSelectedCommunities }))),
                        analyticsContext === AnalyticsContext.ENGAGEMENT && (React.createElement(ProspectSelector, { selectedProspectStatuses: selectedProspectStatuses, updateSelectedProspects: handleSelectProspectStatuses })))))),
        React.createElement(PeekGrid, { item: true, xs: 12, maxWidth: "-webkit-fill-available !important" },
            React.createElement(Outlet, { context: {
                    setLayoutConfig,
                    analyticsContext,
                    selectedCommunities,
                    selectedOrganizations,
                    period,
                    selectedProspectStatus: selectedProspectStatuses[0].toLowerCase(),
                    selectedPlans,
                } }))));
};
